import React, { useRef, useState, useEffect,  useLayoutEffect } from 'react';
import { spectronShader } from './extra/shader.js';
import TokenDetails from './TokenDetails.js';
import { motion } from "framer-motion";

const Canvas = props => {

  // Define references and states
  const printAllLogs = false;
  const canvasRef = useRef(null)
  const counterRef = useRef(0);
  const controlsRef = useRef();
  const generatingRef = useRef(0);
  const tokenRef = useRef(props.token);
  const [isAnimating, setIsAnimating] = useState(props.display !== "canvas" ? true : false);

  // Events
  const handleMouseEnter = () => {
    setIsAnimating(true);
  }
  const handleMouseOut = () => {
    setIsAnimating(false);
  }
  const handleFullscreenOpen = () => {
    props.setFullscreenCanvas({enabled:true, token:tokenRef.current, display:"foreground"});
  }
  const handleFullscreenClose = () => {
    props.setFullscreenCanvas({enabled:false, token:tokenRef.current, display:"foreground"});
  }

  // Hash functions
  const getFromHash = h => {
    h = h.substr(2)
    let table = []
    for (let i = 0; i < 16; i++) { table[i] = parseInt("0x"+(h.substr(i+8, 1) + h.substr(i+24, 1) + h.substr(i+40, 1)),16)/4096 }
    return table
  }

  const hashTableRef = useRef(getFromHash(props.token.hash))

  useEffect(() => {
    hashTableRef.current = getFromHash(props.token.hash)
  },[props.token])

  // Utility functions
  const printWithZeroes = n => {
    if (n < 10) return "00"+n;
    if (n < 100) return "0"+n;
    return n;
  }

  const initWebGL = () => {
   
    const gl = canvasRef.current.getContext('webgl');
    const {width, height} = canvasRef.current.getBoundingClientRect();
    const shader = {frag_code: spectronShader.fragment, vert_code: spectronShader.vertex};
    let uniforms, attributes;
    /* Webgl stuff */
    gl.canvas.width = width;
    gl.canvas.height = height;
    /* Fragment shader */
    shader.frag = gl.createShader(gl.FRAGMENT_SHADER);       
      gl.shaderSource(shader.frag, shader.frag_code);       
      gl.compileShader(shader.frag);
      if (!gl.getShaderParameter(shader.frag, gl.COMPILE_STATUS)) { throw "Fragment shader:" + gl.getShaderInfoLog(shader.frag);}
    /* Vertex shader */
    shader.vert = gl.createShader(gl.VERTEX_SHADER);       
      gl.shaderSource(shader.vert, shader.vert_code);       
      gl.compileShader(shader.vert);
      if (!gl.getShaderParameter(shader.vert, gl.COMPILE_STATUS)) { throw "Vertex shader:" + gl.getShaderInfoLog(shader.vert); }
    /* Link program */
    shader.prog = gl.createProgram();
      gl.attachShader(shader.prog, shader.vert);
      gl.attachShader(shader.prog, shader.frag);
      gl.linkProgram(shader.prog);
      var success = gl.getProgramParameter(shader.prog, gl.LINK_STATUS);
      if (!success) { throw ("Failed to link shaders:" + gl.getProgramInfoLog(shader.prog));}
      gl.detachShader(shader.prog, shader.vert);
      gl.detachShader(shader.prog, shader.frag);
      gl.deleteShader(shader.frag);
      gl.deleteShader(shader.vert);
      gl.useProgram(shader.prog);
    /* Define uniforms for deploy */
    uniforms = {
      "u_resolution": gl.getUniformLocation(shader.prog, "D"),
      "u_time": gl.getUniformLocation(shader.prog, "m"), 
      "u_generating": gl.getUniformLocation(shader.prog, "I"),
      "u_hash": gl.getUniformLocation(shader.prog, "c")
    }
    //  export const GLSLX_NAME_U_HASH = "c"
    //  export const GLSLX_NAME_U_TIME = "m"
    //  export const GLSLX_NAME_U_RESOLUTION = "D"
    //  export const GLSLX_NAME_U_GENERATING = "I"
    
   /*
    uniforms = {
      "u_resolution": gl.getUniformLocation(shader.prog, "u_resolution"),
      "u_time": gl.getUniformLocation(shader.prog, "u_time"), 
      "u_generating": gl.getUniformLocation(shader.prog, "u_generating"),
      "u_hash": gl.getUniformLocation(shader.prog, "u_hash")
    } */
    /* Define attributes */
    attributes = {
      "a_position": gl.getAttribLocation(shader.prog, "position")
    }
    /* Define buffer */
    shader.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, shader.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,-1,1,1,1,1,1,1,-1,-1,-1]), gl.STATIC_DRAW);
    return [gl, uniforms, attributes, shader];
  }

  useLayoutEffect(() => {
    (printAllLogs && console.log("Loading now", props.element, props.display));
    let frameId;
    const [gl, uniforms, attributes, shader] = initWebGL();

  
    const step = (props.display === "canvas" ? 0.03 : 0.02);
    // Render loop
    const render = () => {
      counterRef.current = counterRef.current + step;
      /* Set uniforms and attributes */
      gl.uniform2f(uniforms.u_resolution, gl.canvas.width, gl.canvas.height);
      gl.uniform1f(uniforms.u_time, counterRef.current);
      gl.uniform1f(uniforms.u_generating, generatingRef.current);
      gl.uniform1fv(uniforms.u_hash, hashTableRef.current);
      /* Clear screen and draw quad */
      gl.enableVertexAttribArray(attributes.a_position);
      gl.vertexAttribPointer(attributes.a_position, 2, gl.FLOAT, false, 0, 0);
      gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
      gl.clearColor(0, 0, 0, 0);      
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      frameId = requestAnimationFrame(render);
    }
    // Control functions
    const start = () => {
      (printAllLogs && console.log("Start rendering now", props.element, props.display));
      frameId = requestAnimationFrame(render);
    }
    const stop = () => {
      (printAllLogs && console.log("Stop rendering now", props.element, props.display));
      cancelAnimationFrame(frameId); 
    }
    const clean = () => {
      gl.deleteProgram(shader.prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);
      gl.deleteBuffer(shader.buffer);      
    }
    controlsRef.current = {start, stop, clean};
    // Check for correct ratio
    if (props.display === "canvas" || props.display === "about") {
      canvasRef.current.height = Math.floor(canvasRef.current.width/1.33);
      render();
    }
   
    // Clean up on unmount
    return () => {
      (printAllLogs && console.log("Unloading now", props.element, props.display));
      stop();
      clean();
    }            
  }, []);

  // Effect for animating
  useEffect(() => {
   if (isAnimating) {
    controlsRef.current.start()
   } else {
    controlsRef.current.stop()
   }   
  }, [isAnimating]);
// Render elements
switch (props.display) {
  case "canvas":
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseOut} onClick={handleFullscreenOpen} >
        <canvas ref={canvasRef} />
        <p className="is-overlay is-size-7 mt-3 mr-3 has-text-right">ID.{printWithZeroes(parseFloat(props.token.id)-17000000)}</p>
        {isAnimating && (
        <p className="is-overlay is-size-7 mt-3 ml-3 is-clickable	">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"><path className="fullScreenIcon" d="M24 9h-4v-5h-5v-4h9v9zm-9 15v-4h5v-5h4v9h-9zm-15-9h4v5h5v4h-9v-9zm9-15v4h-5v5h-4v-9h9z"/></svg>
        </p>
        )}
      </div>
    )
    case "about":
      return (
      <div>
          <canvas ref={canvasRef} />
        </div>
      )
  case "foreground":
    return (
      <motion.div 
      onClick={handleFullscreenClose} 
        className="is-canvas-foreground is-clickable" 
        initial={{opacity:0}}
        animate={{opacity:1}}
        transition={{duration:0.2}}
        exit={{opacity:0}}
        >
        <canvas
        ref={canvasRef} 
        />
        <TokenDetails token={tokenRef.current} hasSocials={false} overlay={true} />
      </motion.div>
    )
  case "background":
    return (
      <motion.div
        initial={{opacity:0}}
        animate={{opacity:1}}
        transition={{duration:0.35}}
        exit={{opacity:0}}
      >
      <canvas 
      ref={canvasRef} 
      className="is-canvas-background"
      />
      </motion.div>
    ) 
    }
  }

  export default Canvas;